/**
 * AnalyticsService - Handles communication with the Cloudflare Analytics Worker
 *
 * Features:
 * - Records artwork views when audio starts playing
 * - Fetches view statistics (total, 7-day, 30-day)
 * - Caches stats to minimize API calls
 * - Deduplicates view recordings per session
 */
export class AnalyticsService {
  constructor(workerUrl) {
    // Worker URL - configure based on environment
    this.workerUrl = workerUrl;
    this.enabled = !!workerUrl;

    // Cache for stats to avoid repeated API calls
    this.statsCache = new Map();
    this.cacheDuration = 5 * 60 * 1000; // 5 minutes

    // Track which artworks we've already recorded a view for this session
    this.recordedViews = new Set();

    // In-flight stat requests, keyed by filename. Callers that ask for a
    // filename already being fetched join the existing promise instead of
    // opening a second request - without this, anything polling faster than
    // the round-trip (a per-frame update(), a 1s room check) fires a fresh
    // request every tick, because the cache is only written on completion.
    /** @type {Map<string, Promise<any>>} */
    this.inFlight = new Map();

    // Request timeout
    this.timeout = 5000; // 5 seconds

    if (this.enabled) {
      console.log('Analytics service initialized:', workerUrl);
    }
  }

  /**
   * Record a view for an artwork
   * Only records once per session per artwork
   * @param {string} videoUrl - The video URL or filename
   */
  async recordView(videoUrl) {
    if (!this.enabled) return;

    const filename = this.extractFilename(videoUrl);

    // Don't record duplicate views in same session. Claimed before the await
    // below so concurrent callers for the same file can't all get past it.
    if (this.recordedViews.has(filename)) {
      console.log('View already recorded for:', filename);
      return;
    }
    this.recordedViews.add(filename);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(`${this.workerUrl}/view`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ filename }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        console.log('View recorded for:', filename);

        // Invalidate cache for this artwork so we get fresh stats
        this.statsCache.delete(filename);
      } else {
        console.warn('Failed to record view:', response.status);
        this.recordedViews.delete(filename); // let a later view try again
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.warn('View recording timed out for:', filename);
      } else {
        console.warn('Failed to record view:', error.message);
      }
      this.recordedViews.delete(filename); // let a later view try again
      // Don't block on analytics failures
    }
  }

  /**
   * Get stats for a single artwork
   * @param {string} videoUrl - The video URL or filename
   * @returns {Promise<{total: number, last7Days: number, last30Days: number}>}
   */
  async getStats(videoUrl) {
    if (!this.enabled) {
      return { total: 0, last7Days: 0, last30Days: 0 };
    }

    const filename = this.extractFilename(videoUrl);

    // Check cache
    const cached = this.statsCache.get(filename);
    if (cached && Date.now() - cached.timestamp < this.cacheDuration) {
      return cached.stats;
    }

    // Join an identical request that is already open rather than starting
    // another one. Registered synchronously below, before the first await,
    // so concurrent callers in the same tick can't slip past it.
    const pending = this.inFlight.get(filename);
    if (pending) {
      return pending;
    }

    const request = this.fetchStatsForFilename(filename);
    this.inFlight.set(filename, request);

    try {
      return await request;
    } finally {
      this.inFlight.delete(filename);
    }
  }

  /**
   * Perform the actual single-artwork stats request. Callers should go through
   * getStats(), which adds caching and in-flight coalescing on top of this.
   *
   * @param {string} filename
   * @returns {Promise<{total: number, last7Days: number, last30Days: number, error?: boolean}>}
   */
  async fetchStatsForFilename(filename) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(`${this.workerUrl}/stats/${encodeURIComponent(filename)}`, {
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const stats = await response.json();

      // Cache the result
      this.statsCache.set(filename, {
        stats,
        timestamp: Date.now(),
      });

      return stats;
    } catch (error) {
      if (error.name === 'AbortError') {
        console.warn('Stats fetch timed out for:', filename);
      } else {
        console.warn('Failed to fetch stats for:', filename, error.message);
      }
      return { total: 0, last7Days: 0, last30Days: 0, error: true };
    }
  }

  /**
   * Get stats for multiple artworks in a single request (batch)
   * More efficient when loading many paintings at once
   * @param {string[]} videoUrls - Array of video URLs
   * @returns {Promise<Object.<string, {total: number, last7Days: number, last30Days: number}>>}
   */
  async getBatchStats(videoUrls) {
    if (!this.enabled || videoUrls.length === 0) {
      return {};
    }

    // Dedupe: several painting slots can share one video file
    const filenames = Array.from(new Set(videoUrls.map((url) => this.extractFilename(url))));

    // Check which ones we need to fetch
    const uncached = [];
    /** @type {Array<[string, Promise<any>]>} */
    const joined = []; // already being fetched by someone else
    const results = {};

    for (const filename of filenames) {
      const cached = this.statsCache.get(filename);
      const open = this.inFlight.get(filename);
      if (cached && Date.now() - cached.timestamp < this.cacheDuration) {
        results[filename] = cached.stats;
      } else if (open) {
        // Hold the promise itself - by the time we await it the owner may
        // already have removed its inFlight entry.
        joined.push([filename, open]);
      } else {
        uncached.push(filename);
      }
    }

    // Fetch uncached stats
    if (uncached.length > 0) {
      // Register before the first await so a caller arriving later in this
      // same tick joins this request instead of opening a duplicate. All the
      // filenames in this batch share the one promise.
      const batchRequest = this.fetchBatchStats(uncached);
      for (const filename of uncached) {
        this.inFlight.set(filename, batchRequest);
      }

      try {
        Object.assign(results, await batchRequest);
      } finally {
        for (const filename of uncached) {
          this.inFlight.delete(filename);
        }
      }
    }

    // Pick up whatever the requests we joined produced. Those promises came
    // from either path, so the payload is a filename-keyed batch map or a
    // single stats object.
    for (const [filename, open] of joined) {
      try {
        const payload = await open;
        results[filename] = payload?.[filename] ?? payload ?? { total: 0, last7Days: 0, last30Days: 0 };
      } catch {
        results[filename] = { total: 0, last7Days: 0, last30Days: 0, error: true };
      }
    }

    return results;
  }

  /**
   * Perform the actual batched stats request. Callers should go through
   * getBatchStats(), which adds caching and in-flight coalescing on top.
   *
   * @param {Array<string>} uncached - deduped filenames, none of them cached
   * @returns {Promise<Object.<string, {total: number, last7Days: number, last30Days: number}>>}
   */
  async fetchBatchStats(uncached) {
    const results = {};

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(
        `${this.workerUrl}/stats/all?filenames=${uncached.map(encodeURIComponent).join(',')}`,
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const batchStats = await response.json();

      // Cache and add to results
      for (const [filename, stats] of Object.entries(batchStats)) {
        this.statsCache.set(filename, {
          stats: { filename, ...stats },
          timestamp: Date.now(),
        });
        results[filename] = { filename, ...stats };
      }
    } catch (error) {
      console.warn('Failed to fetch batch stats:', error.message);
      // Return zeros for failed fetches
      for (const filename of uncached) {
        results[filename] = { total: 0, last7Days: 0, last30Days: 0, error: true };
      }
    }

    // A filename the worker simply didn't answer for still needs an entry, or
    // the caller never stamps it as fetched and re-asks on its next tick.
    for (const filename of uncached) {
      if (!results[filename]) {
        results[filename] = { total: 0, last7Days: 0, last30Days: 0, error: true };
      }
    }

    return results;
  }

  /**
   * Extract filename from video URL
   * @param {string} videoUrl - Full URL or path
   * @returns {string} - Just the filename
   */
  extractFilename(videoUrl) {
    // Handle various URL formats
    // "/videos/filename.mp4" -> "filename.mp4"
    // "https://example.com/videos/filename.mp4" -> "filename.mp4"
    return videoUrl.split('/').pop();
  }

  /**
   * Clear the stats cache (useful for forcing refresh)
   */
  clearCache() {
    this.statsCache.clear();
  }

  /**
   * Check if analytics is enabled
   * @returns {boolean}
   */
  isEnabled() {
    return this.enabled;
  }
}

// Singleton instance
let analyticsServiceInstance = null;

/**
 * Get or create the singleton AnalyticsService instance
 * @param {string} [workerUrl] - Worker URL (only needed on first call)
 * @returns {AnalyticsService}
 */
export function getAnalyticsService(workerUrl) {
  if (!analyticsServiceInstance) {
    analyticsServiceInstance = new AnalyticsService(workerUrl);
  }
  return analyticsServiceInstance;
}
